import { ReactNode } from 'react';
export enum ProductType {
  TYPE_MAGAZINE = 1,
  TYPE_ECARD = 2,
  TYPE_POSTCARD = 3,
  TYPE_DIGITAL_GOOD = 4,
  TYPE_BOOK = 5,
  TYPE_MISCELLANEOUS_GOODS = 7,
}

export type Image = {
  url: string;
  alt_text?: string;
  width: number;
  height: number;
  position?: number;
};

export type SEO = {
  no_index?: boolean;
  title?: string;
  description?: string;
  json_ld_extra?: Record<string, unknown>;
};

export type Variant = {
  id: number;
  type_id: number;
  name: string;
  content: string;
};

export type Sku = {
  id: number;
  code: string;
  text: string;
  buyable: boolean;
  msrp: number;
  price: number;
  sale_price?: number;
  /**
   * When the sale price stops applying.
   *
   * Not a field of the sku — it belongs to the offer code that discounted it, and a
   * props builder grafts it on alongside `sale_price` when it applies one. Absent on
   * any sku nobody has discounted.
   */
  valid_until?: string | null;
  variants: Variant[];
  attributes?: Record<string, string | number>;
};

/**
 * The nested product `reviews_data` already carries, mirroring the PHP value object of
 * the same name.
 *
 * Not a second `Product`: it resolves to the parent title for a single issue, and it
 * carries three things a `Product` does not — the description, a gtin derived from the
 * issn, and the publisher's brand — which is exactly what a reviews widget is asked
 * for.
 */
export type ReviewsDataProduct = {
  id: string;
  sku: string;
  name: string;
  description?: string;
  price?: number;
  url?: string;
  image?: string;
  gtin?: string;
  brand?: string;
};

export type ReviewsData = {
  rating: number;
  count: number;
  product?: ReviewsDataProduct;
};

export type Product = {
  id: number;
  slug: string;
  /** Absolute url, generated server-side by ProductUrlGenerator. */
  href: string;
  name: string;
  featured_image: Image;
  product_type: ProductType;
  digital: boolean;
  available: boolean;
  buyable: boolean;
  base_sku: Sku;
  /**
   * Every buyable term. Only on payloads serialized with `storefront-product-expanded`
   * — the category and list payloads carry `base_sku` alone — so a component that
   * needs it is one a product-page props builder feeds.
   */
  active_skus?: Sku[];
  /** The offer code the product applies to itself when the visitor brought none. */
  embedded_offer_code?: string | null;
  reviews_data?: ReviewsData;
  genome_attributes?: Record<string, any>;
  /**
   * Draws the On Sale ribbon on a card. Not a serializer field: `CategoryPageProps` sets it
   * from the legacy row, on the legacy card's own condition — a named coupon on the base
   * sku's vendor sku, which is a digital title on a Zinio or Magzter sale.
   */
  on_sale?: boolean;
};

/**
 * One of the other formats the same title is sold in — the hardcover beside the
 * paperback. Its own product row, with its own slug, url, cover and skus, which is why
 * the format selector navigates rather than just re-pricing.
 */
export type GroupedProduct = Product & {
  active_skus: Sku[];
  product_attributes?: Record<string, any>;
};

/**
 * A product serialized with `storefront-product-expanded` — what a product *page* is
 * given, as against the lean row a card or a band gets.
 *
 * The optionals on `Product` that this page can rely on are re-declared required here,
 * so a component that has an ExpandedProduct does not have to null-check what the
 * builder guarantees.
 */
export type ExpandedProduct = Product & {
  /** HTML, from the publisher. Rendered through dangerouslySetInnerHTML. */
  description?: string;
  images?: Image[];
  /** The leaf category; walk `.parent` for the rest of the trail. */
  category?: Category;
  seo?: SEO;
  active_skus: Sku[];
  product_attributes?: Record<string, any>;
  grouped_products?: GroupedProduct[];
  genome_attributes: Record<string, any>;
};

/**
 * One sku's price under an offer code.
 *
 * Its own list, joined to the product's skus on `sku_code` rather than merged into them
 * server-side — see `applyOfferCodeSkus`, which is the join.
 */
export type OfferCodeSku = {
  sku_code: string;
  price: number;
  valid_until?: string | null;
};

/**
 * What the Apple Pay and Google Pay buttons need from Braintree.
 *
 * Null where the site has no Braintree configuration, which is how both buttons are
 * switched off; `googleMerchantId` null switches off Google Pay alone. headless read
 * the same values out of NEXT_PUBLIC_BRAINTREE_* environment variables.
 */
export type PaymentConfig = {
  clientToken: string;
  environment: string;
  googleMerchantId: string | null;
  /** ISO-2. The only country either wallet will accept a shipping address in. */
  shippingCountry: string;
  /** What the payment sheet calls us. */
  merchantName: string;
  /**
   * PayPal's own settings, or absent where the site does not offer PayPal — which is the
   * gate as well as the configuration. The legacy cart draws its PayPal button inside
   * `{#if:ordering.payment.options.type:eq:PAYPALEXP#}` for the same reason: an express
   * payload for a payment method that is switched off is rejected in `checkout.php` and
   * the customer is bounced back to the login step with nothing said.
   *
   * Left out by the product page, which has no PayPal button.
   */
  paypal?: {
    /** The merchant name PayPal prints on its own sheet. */
    businessName: string | null;
    /**
     * Present when PayPal is to be vaulted as a billing agreement rather than charged
     * once — it is both the flag and the wording shown to the customer.
     */
    billingAgreementDescription: string | null;
  } | null;
};

export type Category = {
  id: number;
  /** Only on payloads serialized with the full `storefront` group; use `href` to link. */
  slug?: string;
  /** Relative to the site root; render it through `buildUrl`, which re-attaches the base url. */
  href: string;
  name: string;
  description?: string;
  image?: Image;
  /** The tile artwork (jss_sections.thumbnail), a different column from `image`. */
  thumbnail?: Image;
  products?: Product[];
  seo?: SEO;
  icon?: string;
  parent?: Category;
  visible: boolean;
};

export type Banner = {
  id: number;
  name: string;
  tag: string;
  url: string;
  image: Image;
  mobile_image?: Image;
  bg_color?: string;
  custom_html?: string;
  sa: boolean;
  open_in_new_tab: boolean;
  no_follow: boolean;
};

export type SearchAlertsResult = {
  r: boolean;
};

/**
 * What CartProps serializes into #dm-v2-cart. Deliberately a summary rather than a
 * cart: V2 needs a line count for the badge and sa.r for banner restriction, and
 * serializing the lines themselves costs a product and sku catalogue query each.
 */
export type Cart = {
  /** Total quantity across the lines — two copies of one title count as two. */
  items_quantity: number;
  sa: SearchAlertsResult;
  /**
   * Absolute, and only on a cart that came back from the api — #dm-v2-cart carries the
   * summary groups, which leave it out. What the wallet buttons navigate to once the
   * item and its payment payload are on the cart.
   */
  checkout_url?: string;
  /**
   * The lines, and likewise only on a cart that came back from the api. Present exactly
   * when `checkout_url` is, and for the same reason: the summary groups leave both out
   * because serializing a line costs a product and a sku catalogue query.
   *
   * A component that draws lines therefore has to fetch rather than read the provider —
   * see `getCart`.
   */
  items?: CartItem[];
  total_items_price?: number;
  total_discounts?: number;
  shipping_total_amount?: number;
  applied_offer_codes?: string[];
  /**
   * The code the visitor's session holds for a product, keyed by product id, for 12
   * hours — which is how a Google automated discount reaches a stateless page. Only on a
   * cart that came back from the api, and `[]` rather than `{}` when there are none,
   * which is how PHP serializes an empty map.
   */
  sticky_offer_codes?: Record<string, string>;
  /**
   * The upgrade this cart is being offered, and only when it was asked for with
   * `?include=lightbox_deal` — see `getCart`. Absent otherwise, and absent when the cart
   * qualifies for nothing.
   */
  lightbox_deal?: LightboxDeal | null;
  /**
   * The visitor's email and its hashes, when one is known — from the signed-in
   * customer, or from the newsletter subscription their visitor record points at.
   * Absent for everyone else, which is most visitors. Built by
   * Cart::getSessionEmailData() and already carried by the storefront cart endpoint.
   */
  session_email_data?: SessionEmailData;
  affiliate_data?: CartAffiliateData;
  visitor_state?: string;
  ab_test_variant?: string | null;
};

/**
 * The "lightbox deal": the offer to lengthen a subscription already in the cart, made
 * once, at the moment the customer commits to checking out.
 *
 * The server has already turned the admin's html copy into strings — see
 * `DMagz\Model\LightboxDealOffer` — because this page renders text, not markup.
 */
export type LightboxDeal = {
  title: string;
  subtitle?: string | null;
  accept_label: string;
  decline_label: string;
  /** Where accepting posts, with `xLBIDs[]` naming the lines being upgraded. */
  apply_url: string;
  /** Where refusing posts. It records the refusal; both urls end at checkout. */
  skip_url: string;
  items: LightboxDealItem[];
};

export type LightboxDealItem = {
  /** The cart line being upgraded — matches `CartItem.id`, and posts as `xLBIDs[]`. */
  item_id: number;
  deal_id: number;
  /** The offer, one string per line the copy asked to break onto. */
  lines: string[];
};

/**
 * One line of the cart. `id` is the line's own id (the legacy `uniqueID`), which is what
 * the item endpoints address — two copies of one title are two lines with two ids.
 */
export type CartItem = {
  id: number;
  quantity: number;
  /**
   * The price of ONE copy, before `discount` — not the line total.
   *
   * `CartItem` serializes `CartContent::getPrice()`, which returns the per-unit `price1`;
   * legacy multiplies by the quantity itself wherever it wants a line
   * (`affiliateTracking.php`: `($prod['price'] - $prod['discount']) * $prod['qty']`). This
   * said "the line total, already multiplied by the quantity", which is wrong and is easy
   * to believe because the api never shows it: asking it for a quantity of 3 answers with
   * three lines of one, so `price` and the line total are the same number on every cart
   * anything here has seen. A reader who trusts the old comment divides by the quantity
   * and is only correct by accident.
   */
  price: number;
  /** The discount on one copy, on the same per-unit basis as `price`. */
  discount: number;
  shipping: number;
  send_as_gift: boolean;
  product: {
    id: number;
    slug?: string;
    name: string;
    product_type?: number;
    digital?: boolean;
    href?: string | null;
    featured_image?: { url: string; alt_text?: string | null } | null;
    /**
     * The product's base sku — the id it is reported to GA4 under, as `base_sku.code`.
     *
     * Not the sku that was bought: `sku` above is the term and type the customer chose, and
     * its code differs from this one on every line but the base term. The cart api has
     * always sent this (`DMagz\Model\Product::$baseSku`, serializer group `storefront`);
     * only this type was too narrow to say so.
     */
    base_sku?: { code: string } | null;
  };
  sku: {
    id?: number;
    code: string;
    price?: number;
    /** The term/type text the catalogue shows for this sku — "1 Year", "2 Years". */
    text?: string;
  };
};

/**
 * What FullLayoutPropsBuilder produces — the contract headless_jshop calls LayoutData.
 *
 * Shared rather than owned by a brand because every brand's `Layouts.Full` is fed by
 * the same builder: a page declares `layoutName = 'Layouts.Full'` and gets its own
 * brand's chrome, and the props it is handed are identical either way. Defining it in
 * one brand and importing it from another would make that brand's layout depend on a
 * sibling it has nothing else to do with.
 */
export type FullLayoutProps = {
  categories: Category[];
  featuredCategories: Category[];
  booksCategories?: Category[];
  featuredBooksCategories?: Category[];
  /**
   * Whether the page wants the sitewide banner. Nothing reads it yet — headless
   * renders <SitewideBanner /> on it and that component is not ported — but it stays
   * in the contract because the builder caches on it, so a page that opts out gets
   * its own entry rather than sharing one with pages that opt in.
   */
  displaySitewideBanner?: boolean;
  /**
   * The year the brand's footer states in its copyright line, as the server rendered it.
   *
   * It comes from the server so that the markup and the payload the browser hydrates
   * from agree: on a full V2 page the footer is inside the page's own server-rendered
   * tree and is cached with it, so a `new Date()` in the footer would disagree with a
   * day-old render and cost a repaint. Every footer hands it to `useCurrentYear`, which
   * corrects it in the browser after that first render.
   *
   * Optional because the legacy `Layouts.Full.Footer` mount passes no layout props at
   * all. That mount is `SsrOptions::disabled()` and renders in the browser, where the
   * clock is the right answer — so the hook falls back to it when this is absent.
   */
  copyrightYear?: number;
  children?: ReactNode;
};

/**
 * What AccountLayoutPropsBuilder produces — the signed-in customer's chrome.
 *
 * Shared for the same reason FullLayoutProps is, and it is the same situation one level
 * up: every brand's `Layouts.Account` is fed by this one builder, so a page that
 * declares `layoutName = 'Layouts.Account'` is handed the identical payload whichever
 * brand draws it.
 *
 * The two fields beyond Full's are the account menu's, and both exist because the
 * auto-renewal service is the brand's own — it is called DiscountLock on DiscountMags
 * and Renew & Save on TopMags, and it lives at a path each brand spells its own way.
 * `activePath` is separate from `autoRenewUrl` because it answers a different question:
 * which entry is current, which a server render cannot work out for itself.
 */
export type AccountLayoutProps = FullLayoutProps & {
  /** The menu entry to mark, as its path — `/my-account/discountlock`. */
  activePath?: string;
  /** The brand's name for its auto-renewal service — 'DiscountLock' on DiscountMags. */
  autoRenewServiceName: string;
  /** Where that entry points, relative to the site root. */
  autoRenewUrl: string;
  children?: ReactNode;
};

/**
 * The hash variants exist because ad platforms each want their own. Consumers should
 * pass the whole object along rather than pick, which is what the GTM push does.
 */
export type SessionEmailData = {
  email: string;
  md5: string;
  sha1: string;
  sha256: string;
  md5UC: string;
  sha1UC: string;
  sha256UC: string;
};

export type CartAffiliateData = {
  username: string;
  external_id: string;
  external_child_id: string;
};

export type Site = {
  id: string;
  /** The display name — "DiscountMags.com", "TopMags.com". */
  name: string;
  brand: string;
  base_url: string;
  currency: string;
  /** US, CA, AU or IN — the same value config.php derives into $jssCountry. */
  country: string;
  /** getenv('ENVIRONMENT'): "production", "development", …; unset counts as production. */
  environment: string;
};

// ---------------------------------------------------------------------------
// Navigation types, ported from headless_jshop (dm-ui/types, headless-jshop/types
// and the handful of jshop-sdk types the navigation referenced). Kept here rather
// than pulling in the SDK, which the navigation only needed for its search client.
// ---------------------------------------------------------------------------

export type Name = {
  first_name: string;
  last_name: string;
};

export type User = {
  id: number;
  name: Name;
  email: string;
};

export type ToggleState = {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
};

export type SearchBarState = ToggleState & {
  isFocused: boolean;
  focus: () => void;
  unfocus: () => void;
};

export type MenuItem = {
  id: number | string;
  href: string;
  title: string;
  icon?: import('@fortawesome/fontawesome-svg-core').IconLookup;
  priceIcon?: {
    price: string;
  };
};

export enum COUNTRY {
  US = 'US',
  CA = 'CA',
  AU = 'AU',
}
